import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ProtocolAnswer } from 'shared/domain/protocolAnswer';
import { sameProtocolTreeId } from './protocolTreeIds';

type ChecklistAnswerConfig = {
  _id: string;
  type: AnswerType;
  deleted?: boolean;
};

export type ChecklistItemAnswerSource = {
  _id: string;
  answers?: ChecklistAnswerConfig[];
};

type ProtocolItemWithTemplate = {
  _id: string;
  answers?: ProtocolAnswer[];
  templateChecklistItem?: string | { _id?: string };
};

function checklistItemIdFromProtocolItem(
  protocolItem: ProtocolItemWithTemplate
): string | undefined {
  const templateChecklistItem = protocolItem.templateChecklistItem;
  if (typeof templateChecklistItem === 'string') {
    return templateChecklistItem;
  }

  return templateChecklistItem?._id;
}

function createApplicabilityStub(
  answerConfig: ChecklistAnswerConfig,
  isApplicable: boolean
): ProtocolAnswer {
  const base = {
    _id: answerConfig._id,
    checklistAnswerId: answerConfig._id,
    isApplicable,
  };

  switch (answerConfig.type) {
    case AnswerType.Numeric:
      return { ...base, type: AnswerType.Numeric };
    case AnswerType.Text:
      return { ...base, type: AnswerType.Text };
    case AnswerType.OptionSelection:
      return { ...base, type: AnswerType.OptionSelection };
    case AnswerType.Compliance:
    default:
      return { ...base, type: AnswerType.Compliance };
  }
}

function mergeAnswersForProtocolItem(
  existingAnswers: ProtocolAnswer[] | undefined,
  answerConfigs: ChecklistAnswerConfig[] | undefined,
  isApplicable: boolean
): ProtocolAnswer[] {
  const activeConfigs = (answerConfigs ?? []).filter(
    (answerConfig) => !answerConfig.deleted
  );

  if (activeConfigs.length === 0) {
    return (existingAnswers ?? []).map((answer) => ({
      ...answer,
      isApplicable,
    }));
  }

  const existingByChecklistAnswerId = new Map(
    (existingAnswers ?? []).map((answer) => [answer.checklistAnswerId, answer])
  );

  const mergedFromTemplate = activeConfigs.map((answerConfig) => {
    const existingAnswer = existingByChecklistAnswerId.get(answerConfig._id);
    if (existingAnswer) {
      existingByChecklistAnswerId.delete(answerConfig._id);
      return {
        ...existingAnswer,
        isApplicable,
      };
    }

    return createApplicabilityStub(answerConfig, isApplicable);
  });

  const leftoverAnswers = [...existingByChecklistAnswerId.values()].map(
    (answer) => ({
      ...answer,
      isApplicable,
    })
  );

  return [...mergedFromTemplate, ...leftoverAnswers];
}

export function mergeSectionApplicabilityIntoProtocol<
  T extends ProtocolItemWithTemplate,
>(
  protocol: T[],
  protocolItemIds: string[],
  isApplicable: boolean,
  checklistItems?: ChecklistItemAnswerSource[]
): T[] {
  if (protocolItemIds.length === 0) {
    return protocol;
  }

  return protocol.map((protocolItem) => {
    const matchesItem = protocolItemIds.some((protocolItemId) =>
      sameProtocolTreeId(protocolItemId, protocolItem._id)
    );
    if (!matchesItem) {
      return protocolItem;
    }

    const checklistItemId = checklistItemIdFromProtocolItem(protocolItem);
    const checklistItem = checklistItems?.find((item) =>
      sameProtocolTreeId(item._id, checklistItemId)
    );

    return {
      ...protocolItem,
      answers: mergeAnswersForProtocolItem(
        protocolItem.answers,
        checklistItem?.answers,
        isApplicable
      ),
    };
  });
}
